import { Grid, GridItem, Tooltip, Text } from '@chakra-ui/react';
import {
  SupplierCoverageHeatmapStatistics,
  ArticleCoverageHeatmapStatistics,
} from '@texas/api/endpoints/statisticsApi';
import React from 'react';
import { SupplierCoverageHeatmapViewMode } from '../shared/types';
import { HeatmapCell } from './HeatmapCell';

interface SupplierGridProps {
  data:
    | SupplierCoverageHeatmapStatistics
    | ArticleCoverageHeatmapStatistics
    | null;
  viewMode: SupplierCoverageHeatmapViewMode;
  isLoading: boolean;
  handleViewSuppliers: (data: {
    supplierIds: number[];
    branchName: string;
    productGroupName: string;
  }) => void;
  handleViewArticles: (data: {
    branchId: number;
    branchName: string;
    productGroupId: number;
    productGroupName: string;
  }) => void;
}

export function SupplierGrid({
  data,
  viewMode,
  isLoading,
  handleViewSuppliers,
  handleViewArticles,
}: SupplierGridProps) {
  // Calculate max count for color scaling
  const maxCount = data
    ? Math.max(
        ...data.branches.flatMap((b) =>
          b.cells.map((c) =>
            viewMode === 'suppliers'
              ? c.supplierCount ?? 0
              : c.articleCount ?? 0,
          ),
        ),
        1, // Minimum of 1 to avoid division by zero
      )
    : 1;

  return (
    <Grid
      key={2}
      templateColumns={`150px repeat(${data?.branches.length ?? 0}, 24px)`}
      gap={2}
    >
      <GridItem h="80px" />
      {data?.branches.map((branch) => (
        <Tooltip key={branch.branchId} label={branch.branchName}>
          <GridItem
            fontSize="12px"
            textAlign="left"
            transform="rotate(-45deg)"
            transformOrigin="bottom left"
            whiteSpace="nowrap"
            h="80px"
            display="flex"
            alignItems="flex-end"
            pl="2px"
          >
            {branch.branchIdentifier}
          </GridItem>
        </Tooltip>
      ))}

      {data?.productGroups.map((pg) => (
        <React.Fragment key={pg.productGroupId}>
          <GridItem
            key={`pg-${pg.productGroupId}`}
            fontSize="xs"
            pr={2}
            textAlign="right"
          >
            <Text noOfLines={1}>{pg.productGroupValue}</Text>
          </GridItem>

          {data.branches.map((branch) => {
            const cell = branch.cells.find(
              (c) => c.productGroupId === pg.productGroupId,
            );
            const count =
              viewMode === 'suppliers'
                ? cell?.supplierCount ?? 0
                : cell?.articleCount ?? 0;
            const ids =
              viewMode === 'suppliers'
                ? cell?.supplierIds ?? []
                : cell?.articleIds ?? [];

            const handleClick = () => {
              if (isLoading) return;
              if (viewMode === 'suppliers') {
                handleViewSuppliers({
                  supplierIds: ids,
                  branchName: branch.branchIdentifier,
                  productGroupName: pg.productGroupValue,
                });
              } else {
                handleViewArticles({
                  branchId: branch.branchId,
                  branchName: branch.branchIdentifier,
                  productGroupId: pg.productGroupId,
                  productGroupName: pg.productGroupValue,
                });
              }
            };

            return (
              <HeatmapCell
                key={`${pg.productGroupId}-${branch.branchId}`}
                count={count}
                ids={ids}
                maxCount={maxCount}
                viewMode={viewMode}
                branchId={branch.branchId}
                branchName={branch.branchName}
                branchIdentifier={branch.branchIdentifier}
                productGroupId={pg.productGroupId}
                productGroupName={pg.productGroupValue}
                onClick={handleClick}
              />
            );
          })}
        </React.Fragment>
      ))}
    </Grid>
  );
}
